import {
  AMENDMENT_ANALYSIS_TYPES,
  AmendmentAnalysisStatus,
  GeneralAnalysisType,
} from '../../interfaces/generalAnalysis'
import { FilterFieldType, IFilter } from './types'

// Amendment-only: this filter config drives the Operations → Amendments list,
// so non-amendment types (e.g. OPS Write Up) have no label here.
const TYPE_LABELS: Partial<Record<GeneralAnalysisType, string>> = {
  [GeneralAnalysisType.AmendmentABL]: 'ABL',
  [GeneralAnalysisType.AmendmentTermLoan]: 'Term Loan',
  [GeneralAnalysisType.AmendmentOveradvance]: 'Overadvance',
  [GeneralAnalysisType.AmendmentEquipmentLoan]: 'Equipment Loan',
}

const TYPE_OPTIONS = AMENDMENT_ANALYSIS_TYPES.map((type) => ({
  value: type,
  label: TYPE_LABELS[type] ?? type,
}))

const STATUS_OPTIONS = Object.values(AmendmentAnalysisStatus).map((status) => ({
  value: status,
  label: status,
}))

/**
 * Columns for the Operations → Amendments list. Drives the sortable
 * headers (`TableFiltersRow`) and filter bar (`FilterContainer`). Same
 * `dbField + alias` pattern as `activities/List.ts`: each filterable /
 * sortable column points at the actual SQL column on the appropriate
 * alias, so `Base.buildFiltersQuery` can emit both WHERE and ORDER BY
 * directly. The service overrides ORDER BY only for `type` / `status`
 * to use CASE-based workflow ordering instead of alphabetical.
 *
 * Aliases (matching `generalAnalysis/List.ts`):
 *  - `amendment`  → `client_amendments` (FROM root)
 *  - `analysis`   → `general_analyses` (joined via `aad.analysis_id`)
 *  - `aad`        → `amendment_analysis_details`
 */
export const AMENDMENTS_LIST_FILTERS_CONFIG = [
  {
    field: 'expand',
    title: '',
    type: FilterFieldType.Empty,
  },
  {
    field: 'type',
    dbField: 'type',
    alias: 'analysis',
    type: FilterFieldType.List,
    isMultiple: true,
    isSortable: true,
    title: 'Analysis Type',
    options: TYPE_OPTIONS,
  },
  {
    field: 'clientName',
    dbField: 'client_name',
    alias: 'amendment',
    type: FilterFieldType.List,
    isMultiple: true,
    isSortable: true,
    title: 'Client',
  },
  {
    field: 'assignee',
    type: FilterFieldType.Empty,
    title: 'Assignee',
    isSortable: false,
  },
  {
    field: 'startDate',
    dbField: 'start_date',
    alias: 'analysis',
    type: FilterFieldType.Empty,
    align: 'right',
    isSortable: true,
    title: 'Start Date',
  },
  {
    field: 'status',
    dbField: 'status',
    alias: 'analysis',
    type: FilterFieldType.List,
    isMultiple: true,
    isSortable: true,
    title: 'Status',
    options: STATUS_OPTIONS,
  },
  {
    // Computed server-side in generalAnalysis/List from the linked
    // amendment / term loan / overadvance — not a real column.
    field: 'summaryOfChanges',
    type: FilterFieldType.Empty,
    isSortable: false,
    isCustom: true,
    title: 'Summary of Changes',
  },
  {
    field: 'effectiveDate',
    dbField: 'effective_date',
    alias: 'aad',
    type: FilterFieldType.Empty,
    align: 'right',
    isSortable: true,
    title: 'Effective Date',
  },
  {
    // Trailing column holding the row's 3-dot delete menu. `isCustom`
    // keeps Base from treating it as a real filterable / sortable
    // column while still reserving a header cell for layout alignment.
    field: 'actions',
    type: FilterFieldType.Empty,
    isSortable: false,
    isCustom: true,
    title: '',
  },
  {
    // Synthetic boolean: backs the "Active" quick filter chip (default).
    // Active = has an analysis AND its status is not terminal. Executed
    // and Archived amendments are intentionally hidden so the queue
    // surfaces only in-progress work; users see them via "All".
    field: 'hasAnalysis',
    dbField: `(analysis.id IS NOT NULL AND analysis.status NOT IN ('${AmendmentAnalysisStatus.Executed}', '${AmendmentAnalysisStatus.Archived}'))`,
    type: FilterFieldType.Boolean,
    excludeColumn: true,
    excludeFullSearchFilter: true,
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'All',
        filters: {},
      },
      {
        title: 'Active',
        filters: {
          hasAnalysis: 'true',
        },
      },
    ],
  },
] as const satisfies IFilter[]
